import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';
import { SecretInput } from '@/shared/ui/secret-input';

interface TakeOrderDialogProps {
  submitting: boolean;
  error_text: string | null;
  on_close: () => void;
  on_submit: (args: { password: string }) => void | Promise<void>;
}

export function TakeOrderDialog({
  submitting,
  error_text,
  on_close,
  on_submit,
}: TakeOrderDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [local_error, setLocalError] = useState<string | null>(null);

  const can_submit = !submitting && password.length > 0;

  const onClickSubmit = async (): Promise<void> => {
    const secret = password;
    if (!secret) {
      setLocalError(t('doctor_pos.take_order_credentials_required'));
      return;
    }
    setLocalError(null);
    await on_submit({ password: secret });
  };

  const error = local_error ?? error_text;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('doctor_pos.take_order_modal_title')}</CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('doctor_pos.take_order_modal_hint')}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="take-order-password">{t('auth.password_label')}</Label>
            <SecretInput
              id="take-order-password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('doctor_pos.take_order')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
